import { useEffect, useState } from 'react';
import Button from '../Button/Button';
import AlertModal from '../AlertModal/AlertModal';
import TwoFactorSetup from './TwoFactorSetup';
import './TwoFactor.scss';

/**
 * Gestão do 2FA a partir do Perfil (sessão já autenticada) — usa `axiosAuth` (já tem o
 * Authorization: Bearer da sessão atual) para /auth/2fa/status|disable|recovery-codes, e
 * reutiliza TwoFactorSetup (passando o access_token atual como `token`, que
 * _resolve_setup_user aceita tal como um setup_token) para a ativação opcional.
 */
export default function TwoFactorManage({ axiosAuth, sessionToken }) {
  const [status, setStatus] = useState(null); // {enabled, required}
  const [mode, setMode] = useState('status'); // status | enabling | disabling | regenerating
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [newCodes, setNewCodes] = useState(null);

  async function fetchStatus() {
    setLoading(true);
    try {
      const { data } = await axiosAuth.get('/auth/2fa/status');
      setStatus(data);
    } catch (err) {
      setError('Não foi possível obter o estado do 2FA.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetToStatus() {
    setMode('status');
    setPassword('');
    setCode('');
    setNewCodes(null);
    setError(null);
  }

  async function handleDisable(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await axiosAuth.post('/auth/2fa/disable', { password, code });
      resetToStatus();
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível desativar o 2FA.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRegenerate(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await axiosAuth.post('/auth/2fa/recovery-codes/regenerate', {
        code,
      });
      setNewCodes(data.recovery_codes);
    } catch (err) {
      setError(err.response?.data?.detail || 'Código inválido.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="two-factor-flow">
        <p>A carregar...</p>
      </div>
    );
  }

  if (mode === 'enabling') {
    return (
      <TwoFactorSetup
        token={sessionToken}
        axiosAuth={axiosAuth}
        onDone={() => {
          resetToStatus();
          fetchStatus();
        }}
        onCancel={resetToStatus}
      />
    );
  }

  if (newCodes) {
    return (
      <div className="two-factor-flow">
        <h3>Novos códigos de recuperação</h3>
        <p className="two-factor-hint">
          Os códigos anteriores deixaram de funcionar. Guarda estes — não vão voltar a
          ser mostrados.
        </p>
        <div className="recovery-codes-grid">
          {newCodes.map((c) => (
            <code key={c}>{c}</code>
          ))}
        </div>
        <div className="two-factor-actions">
          <Button onClick={resetToStatus}>Já guardei os códigos</Button>
        </div>
      </div>
    );
  }

  if (mode === 'disabling') {
    return (
      <div className="two-factor-flow">
        {error && (
          <AlertModal
            variant="danger"
            title="Aviso"
            message={error}
            onClose={() => setError(null)}
          />
        )}
        <h3>Desativar 2FA</h3>
        <p className="two-factor-hint">
          {status?.required
            ? 'A tua função exige 2FA — no próximo login vai ser pedido para o configurar outra vez.'
            : 'Confirma a tua password e um código válido para desativar.'}
        </p>
        <form onSubmit={handleDisable} className="two-factor-code-form">
          <input
            className="input"
            type="password"
            placeholder="Password atual"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={busy}
          />
          <input
            className="input"
            type="text"
            placeholder="Código de 6 dígitos ou de recuperação"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            disabled={busy}
          />
          <div className="two-factor-actions">
            <Button type="submit" disabled={busy}>
              Desativar
            </Button>
            <Button type="button" variant="secondary" onClick={resetToStatus}>
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    );
  }

  if (mode === 'regenerating') {
    return (
      <div className="two-factor-flow">
        {error && (
          <AlertModal
            variant="danger"
            title="Aviso"
            message={error}
            onClose={() => setError(null)}
          />
        )}
        <h3>Gerar novos códigos de recuperação</h3>
        <p className="two-factor-hint">
          Os 8 códigos atuais deixam de funcionar assim que gerares novos.
        </p>
        <form onSubmit={handleRegenerate} className="two-factor-code-form">
          <input
            className="input"
            type="text"
            inputMode="numeric"
            placeholder="Código de 6 dígitos"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            required
            disabled={busy}
            autoFocus
          />
          <div className="two-factor-actions">
            <Button type="submit" disabled={busy || code.length < 6}>
              Gerar
            </Button>
            <Button type="button" variant="secondary" onClick={resetToStatus}>
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // mode === 'status'
  return (
    <div className="two-factor-flow">
      {error && (
        <AlertModal
          variant="danger"
          title="Aviso"
          message={error}
          onClose={() => setError(null)}
        />
      )}
      <h3>Verificação em duas etapas</h3>
      <p className="two-factor-hint">
        {status?.enabled
          ? 'O 2FA está ativo na tua conta.'
          : status?.required
          ? 'A tua função exige 2FA — vai ser pedido no próximo login se não ativares agora.'
          : 'O 2FA está desativado. Podes ativá-lo para maior segurança.'}
      </p>
      <div className="two-factor-actions">
        {status?.enabled ? (
          <>
            <Button variant="secondary" onClick={() => setMode('regenerating')}>
              Novos códigos de recuperação
            </Button>
            <Button variant="secondary" onClick={() => setMode('disabling')}>
              Desativar
            </Button>
          </>
        ) : (
          <Button onClick={() => setMode('enabling')}>Ativar 2FA</Button>
        )}
      </div>
    </div>
  );
}
